import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Plus, Shield, AlertTriangle, CheckCircle2, Clock, 
  Phone, Mail, Globe, FileText, Trash2, Edit, Calendar, Package, Sparkles, Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WARRANTY_CATEGORIES = [
  { value: 'appliance', label: 'Appliance' },
  { value: 'fixture', label: 'Fixture' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'windows_doors', label: 'Windows/Doors' },
  { value: 'other', label: 'Other' }
];

const WARRANTY_TYPES = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'extended', label: 'Extended' },
  { value: 'labor', label: 'Labor Only' }
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  expiring_soon: { label: 'Expiring Soon', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: Clock },
  claimed: { label: 'Claimed', color: 'bg-blue-100 text-blue-700', icon: Shield }
};

export default function WarrantyTrackerPage() {
  const darkMode = document.documentElement.classList.contains('dark');
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingWarranty, setEditingWarranty] = useState(null);
  const [project, setProject] = useState(null);
  const [productWarranties, setProductWarranties] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [formData, setFormData] = useState({
    product_name: '',
    category: 'appliance',
    manufacturer: '',
    model_number: '',
    serial_number: '',
    purchase_date: '',
    installation_date: '',
    warranty_start_date: '',
    warranty_length_months: 12,
    warranty_type: 'manufacturer',
    coverage_details: '',
    claim_phone: '',
    claim_email: '',
    claim_website: '',
    claim_instructions: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [warrantiesRes, productWarrantiesRes, projectRes] = await Promise.all([
        axios.get(`${API}/api/projects/${projectId}/warranties`),
        axios.get(`${API}/api/projects/${projectId}/product-warranties`),
        axios.get(`${API}/api/projects/${projectId}`)
      ]);
      setWarranties(warrantiesRes.data.warranties || []);
      setProductWarranties(productWarrantiesRes.data.product_warranties || []);
      setProject(projectRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load warranties');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.product_name || !formData.manufacturer || !formData.warranty_start_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingWarranty) {
        const res = await axios.put(`${API}/api/projects/${projectId}/warranties/${editingWarranty.id}`, formData);
        setWarranties(warranties.map(w => w.id === editingWarranty.id ? res.data.warranty : w));
        toast.success('Warranty updated');
      } else {
        const res = await axios.post(`${API}/api/projects/${projectId}/warranties`, formData);
        setWarranties([...warranties, res.data.warranty]);
        toast.success('Warranty added');
      }
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save warranty');
    }
  };

  const handleDelete = async (warrantyId) => {
    if (!window.confirm('Delete this warranty record?')) return;
    try {
      await axios.delete(`${API}/api/projects/${projectId}/warranties/${warrantyId}`);
      setWarranties(warranties.filter(w => w.id !== warrantyId));
      toast.success('Warranty deleted');
    } catch (error) {
      toast.error('Failed to delete warranty');
    }
  };

  const resetForm = () => {
    setFormData({
      product_name: '',
      category: 'appliance',
      manufacturer: '',
      model_number: '',
      serial_number: '',
      purchase_date: '',
      installation_date: '',
      warranty_start_date: '',
      warranty_length_months: 12,
      warranty_type: 'manufacturer',
      coverage_details: '',
      claim_phone: '',
      claim_email: '',
      claim_website: '',
      claim_instructions: '',
      notes: ''
    });
    setEditingWarranty(null);
  };

  const openEditDialog = (warranty) => {
    setEditingWarranty(warranty);
    setFormData({
      product_name: warranty.product_name || '',
      category: warranty.category || 'appliance',
      manufacturer: warranty.manufacturer || '',
      model_number: warranty.model_number || '',
      serial_number: warranty.serial_number || '',
      purchase_date: warranty.purchase_date?.split('T')[0] || '',
      installation_date: warranty.installation_date?.split('T')[0] || '',
      warranty_start_date: warranty.warranty_start_date?.split('T')[0] || '',
      warranty_length_months: warranty.warranty_length_months || 12,
      warranty_type: warranty.warranty_type || 'manufacturer',
      coverage_details: warranty.coverage_details || '',
      claim_phone: warranty.claim_phone || '',
      claim_email: warranty.claim_email || '',
      claim_website: warranty.claim_website || '',
      claim_instructions: warranty.claim_instructions || '',
      notes: warranty.notes || ''
    });
    setShowAddDialog(true);
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  // Summary stats - combine both product warranties and manual warranties
  const allWarranties = [...productWarranties, ...warranties];
  const activeCount = warranties.filter(w => w.status === 'active').length;
  const expiringCount = warranties.filter(w => w.status === 'expiring_soon').length;
  const expiredCount = warranties.filter(w => w.status === 'expired').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#70170F]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#F5F3EF]'}`}>
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1918]">Warranty Tracker</h1>
            <p className="text-muted-foreground">{project?.name || 'Project'}</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-100">
                <Sparkles className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-teal-600">{productWarranties.length}</p>
                <p className="text-xs text-muted-foreground">From Selections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Shield className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{warranties.length}</p>
                <p className="text-xs text-muted-foreground">Manual</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{activeCount}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{expiringCount}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
                <p className="text-xs text-muted-foreground">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Warranties Tabs */}
      <div className="max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="products" className="gap-2">
                <Sparkles className="w-4 h-4" />
                Product Warranties ({productWarranties.length})
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2">
                <Shield className="w-4 h-4" />
                Manual ({warranties.length})
              </TabsTrigger>
              <TabsTrigger value="labor" className="gap-2">
                <Users className="w-4 h-4" />
                Labor
              </TabsTrigger>
            </TabsList>
            {activeTab === 'manual' && (
              <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-[#70170F] hover:bg-[#8B1E14]">
                <Plus className="w-4 h-4 mr-2" />
                Add Warranty
              </Button>
            )}
          </div>

          {/* Product Warranties Tab - Auto-derived from clips with SpecNum */}
          <TabsContent value="products">
            {productWarranties.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-medium mb-2">No Product Warranties Yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Product warranties are automatically created from approved selections (items with SpecNum)
                  </p>
                  <Button variant="outline" onClick={() => navigate(`/projects/${projectId}/hub`)}>
                    Go to Project Hub
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                <Card className="border-teal-200 bg-teal-50/50">
                  <CardContent className="p-3">
                    <p className="text-sm text-teal-700 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span>These warranties are auto-derived from your approved selections. Labor warranties are tracked in the <button className="underline font-medium" onClick={() => navigate('/contractors')}>Contractor Manager</button>.</span>
                    </p>
                  </CardContent>
                </Card>
                {productWarranties.map(pw => (
                  <Card key={pw.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          {pw.image_url ? (
                            <img src={pw.image_url} alt={pw.product_name} className="w-16 h-16 object-cover rounded-lg" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-teal-100 flex items-center justify-center">
                              <Package className="w-8 h-8 text-teal-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{pw.product_name}</h3>
                              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                                {pw.spec_num}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {pw.manufacturer || 'Unknown Manufacturer'} • {pw.room || pw.category}
                            </p>
                            {pw.model_number && (
                              <p className="text-xs text-muted-foreground mt-1">Model: {pw.model_number}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Approved: {pw.approval_date ? new Date(pw.approval_date).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {pw.warranty_length_months} mo warranty
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Manual Warranties Tab */}
          <TabsContent value="manual">
            {warranties.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-medium mb-2">No Manual Warranties</h3>
                  <p className="text-sm text-muted-foreground mb-4">Add warranties for items not tracked as selections</p>
                  <Button onClick={() => setShowAddDialog(true)} className="bg-[#70170F] hover:bg-[#8B1E14]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Warranty
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {warranties.map(warranty => {
                  const statusConfig = STATUS_CONFIG[warranty.status] || STATUS_CONFIG.active;
                  const StatusIcon = statusConfig.icon;
                  const daysUntil = getDaysUntilExpiry(warranty.warranty_end_date);
                  
                  return (
                    <Card key={warranty.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                              <Package className={`w-6 h-6 ${statusConfig.color.split(' ')[1]}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{warranty.product_name}</h3>
                                <Badge className={statusConfig.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {warranty.manufacturer} • {WARRANTY_CATEGORIES.find(c => c.value === warranty.category)?.label || warranty.category}
                              </p>
                              <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                                {warranty.model_number && <span>Model: {warranty.model_number}</span>}
                                {warranty.serial_number && <span>S/N: {warranty.serial_number}</span>}
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                              Expires: {new Date(warranty.warranty_end_date).toLocaleDateString()}
                              {daysUntil !== null && (
                                <span className={`ml-1 ${daysUntil < 0 ? 'text-red-600' : daysUntil <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                                  ({daysUntil < 0 ? `${Math.abs(daysUntil)}d ago` : `${daysUntil}d left`})
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {warranty.claim_phone && (
                          <a href={`tel:${warranty.claim_phone}`} className="p-2 hover:bg-slate-100 rounded-lg" title="Call">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                          </a>
                        )}
                        {warranty.claim_email && (
                          <a href={`mailto:${warranty.claim_email}`} className="p-2 hover:bg-slate-100 rounded-lg" title="Email">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                          </a>
                        )}
                        {warranty.claim_website && (
                          <a href={warranty.claim_website} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg" title="Website">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                          </a>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(warranty)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(warranty.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </TabsContent>

      {/* Labor Warranties Tab */}
      <TabsContent value="labor">
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="font-medium mb-2">Labor Warranties</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Labor warranties are tracked in the Contractor Manager. Each contractor's warranty terms are stored with their profile.
            </p>
            <Button variant="outline" onClick={() => navigate('/contractors')}>
              <Users className="w-4 h-4 mr-2" />
              Go to Contractor Manager
            </Button>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) { resetForm(); } setShowAddDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWarranty ? 'Edit Warranty' : 'Add Warranty'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Product Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                  placeholder="e.g., Samsung Refrigerator"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WARRANTY_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Manufacturer *</Label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                  placeholder="e.g., Samsung"
                />
              </div>
              <div>
                <Label>Model Number</Label>
                <Input
                  value={formData.model_number}
                  onChange={(e) => setFormData({...formData, model_number: e.target.value})}
                />
              </div>
              <div>
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Installation Date</Label>
                <Input
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) => setFormData({...formData, installation_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Warranty Start Date *</Label>
                <Input
                  type="date"
                  value={formData.warranty_start_date}
                  onChange={(e) => setFormData({...formData, warranty_start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Warranty Length (Months)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.warranty_length_months}
                  onChange={(e) => setFormData({...formData, warranty_length_months: parseInt(e.target.value) || 12})}
                />
              </div>
            </div>

            {/* Warranty Type */}
            <div>
              <Label>Warranty Type</Label>
              <Select value={formData.warranty_type} onValueChange={(v) => setFormData({...formData, warranty_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WARRANTY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Coverage Details */}
            <div>
              <Label>Coverage Details</Label>
              <Textarea
                value={formData.coverage_details}
                onChange={(e) => setFormData({...formData, coverage_details: e.target.value})}
                placeholder="What's covered under this warranty..."
                rows={2}
              />
            </div>

            {/* Claim Info */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Claim Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.claim_phone}
                    onChange={(e) => setFormData({...formData, claim_phone: e.target.value})}
                    placeholder="1-800-SAMSUNG"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.claim_email}
                    onChange={(e) => setFormData({...formData, claim_email: e.target.value})}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.claim_website}
                    onChange={(e) => setFormData({...formData, claim_website: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="col-span-2">
                  <Label>Claim Instructions</Label>
                  <Textarea
                    value={formData.claim_instructions}
                    onChange={(e) => setFormData({...formData, claim_instructions: e.target.value})}
                    placeholder="Steps to file a warranty claim..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#70170F] hover:bg-[#8B1E14]">
              {editingWarranty ? 'Update' : 'Add'} Warranty
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
